import { createClient, Session } from '@supabase/supabase-js'

import {
  AuthStateChangeCallback,
  AuthStateChangeEvent,
  AuthStateChangeSubscription,
  AuthTokenProvider,
  NormalizedAuthSession,
} from './authTypes'
import { decodeJWTClaims, decodeWorkosClaims, workosAuthClient } from './workosAuth'

import type { AppDispatch } from '@/store'
import { resetTenantState } from '@/store/resetTenantState'
import { clearSentryUser } from '@/utils/sentry'

export type {
  AuthStateChangeCallback,
  AuthStateChangeEvent,
  AuthStateChangeSubscription,
  NormalizedAuthSession,
  NormalizedAuthUser,
} from './authTypes'
export { AuthTokenProvider } from './authTypes'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseKey = import.meta.env.VITE_SUPABASE_KEY || ''
const AUTH_PROVIDER_STORAGE_KEY = 'auth.provider'

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    storage: localStorage,
  },
})

const getStoredAuthProvider = (): AuthTokenProvider | null => {
  const provider = localStorage.getItem(AUTH_PROVIDER_STORAGE_KEY)
  return provider === AuthTokenProvider.Supabase || provider === AuthTokenProvider.WorkOS ? provider : null
}

const setStoredAuthProvider = (provider: AuthTokenProvider): void => {
  localStorage.setItem(AUTH_PROVIDER_STORAGE_KEY, provider)
}

const clearStoredAuthProvider = (): void => {
  localStorage.removeItem(AUTH_PROVIDER_STORAGE_KEY)
}

const getCurrentAuthProvider = (): AuthTokenProvider | null =>
  getStoredAuthProvider() ?? (workosAuthClient.hasSession() ? AuthTokenProvider.WorkOS : null)

const toNormalizedSupabaseSession = (session: Session): NormalizedAuthSession => {
  const email = session.user?.email || ''
  const fullName = (session.user?.user_metadata?.full_name as string | undefined) ?? null
  return {
    provider: AuthTokenProvider.Supabase,
    accessToken: session.access_token,
    refreshToken: session.refresh_token,
    expiresAt: session.expires_at ?? null,
    user: {
      id: session.user?.id || null,
      providerUserId: session.user?.id || null,
      email,
      name: fullName?.trim() || email.split('@')[0] || 'User',
      avatarUrl: (session.user?.user_metadata?.avatar_url as string | undefined) ?? null,
      provider: AuthTokenProvider.Supabase,
    },
  }
}

const getSupabaseSession = async (): Promise<NormalizedAuthSession | null> => {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  return session ? toNormalizedSupabaseSession(session) : null
}

export const getAuthSession = async (): Promise<NormalizedAuthSession | null> => {
  const provider = getCurrentAuthProvider()
  if (provider === AuthTokenProvider.WorkOS) {
    const session = await workosAuthClient.getSession()
    if (!session) clearStoredAuthProvider()
    return session
  }
  if (provider === AuthTokenProvider.Supabase) {
    const session = await getSupabaseSession()
    if (!session) clearStoredAuthProvider()
    return session
  }

  const workosSession = await workosAuthClient.getSession()
  if (workosSession) {
    setStoredAuthProvider(AuthTokenProvider.WorkOS)
    return workosSession
  }
  const supabaseSession = await getSupabaseSession()
  if (supabaseSession) setStoredAuthProvider(AuthTokenProvider.Supabase)
  return supabaseSession
}

export const getAuthSessionSnapshot = async (): Promise<NormalizedAuthSession | null> => {
  const provider = getCurrentAuthProvider()
  if (provider === AuthTokenProvider.WorkOS) return workosAuthClient.getSessionSnapshot()
  if (provider === AuthTokenProvider.Supabase) return getSupabaseSession()

  const workosSession = workosAuthClient.getSessionSnapshot()
  if (workosSession) {
    setStoredAuthProvider(AuthTokenProvider.WorkOS)
    return workosSession
  }
  const supabaseSession = await getSupabaseSession()
  if (supabaseSession) setStoredAuthProvider(AuthTokenProvider.Supabase)
  return supabaseSession
}

export const getAccessToken = async (): Promise<string | null> => {
  const session = await getAuthSession()
  if (!session || (session.expiresAt !== null && session.expiresAt <= Date.now() / 1000)) return null
  return session.accessToken
}

export const setAuthSession = async ({
  access_token,
  refresh_token,
}: {
  access_token: string
  refresh_token: string
}): Promise<{ data: { session: NormalizedAuthSession | null }; error: Error | null }> => {
  const claims = decodeJWTClaims(access_token)
  if (!claims || typeof claims.iss !== 'string') {
    return { data: { session: null }, error: new Error('Invalid authentication token') }
  }

  if (decodeWorkosClaims(access_token)) {
    setStoredAuthProvider(AuthTokenProvider.WorkOS)
    await supabase.auth.signOut({ scope: 'local' })
    const session = await workosAuthClient.setSession(access_token, refresh_token)
    if (!session) {
      clearStoredAuthProvider()
      return { data: { session: null }, error: new Error('Invalid WorkOS session') }
    }
    return { data: { session }, error: null }
  }

  const previousProvider = getStoredAuthProvider()
  setStoredAuthProvider(AuthTokenProvider.Supabase)
  const response = await supabase.auth.setSession({ access_token, refresh_token })
  if (response.error) {
    if (previousProvider) setStoredAuthProvider(previousProvider)
    else clearStoredAuthProvider()
    return { data: { session: null }, error: response.error }
  }

  await workosAuthClient.clear()
  setStoredAuthProvider(AuthTokenProvider.Supabase)
  return {
    data: { session: response.data.session ? toNormalizedSupabaseSession(response.data.session) : null },
    error: null,
  }
}

export const refreshAuthSession = async (): Promise<{ survived: boolean }> => {
  const provider = getCurrentAuthProvider()
  if (provider === AuthTokenProvider.WorkOS) {
    const result = await workosAuthClient.refreshSession()
    return { survived: !result.definitiveFailure }
  }

  const { data, error } = await supabase.auth.refreshSession()
  if (!error) return { survived: !!data.session }
  if ((error as { name?: string }).name === 'AuthRetryableFetchError') return { survived: true }
  const status = (error as { status?: number }).status
  return { survived: !(typeof status === 'number' && status > 0 && status < 500) }
}

export const subscribeAuthStateChange = (callback: AuthStateChangeCallback): AuthStateChangeSubscription => {
  const workosSubscription = workosAuthClient.subscribe((event, session) => {
    if (session && getStoredAuthProvider() !== AuthTokenProvider.Supabase) {
      setStoredAuthProvider(AuthTokenProvider.WorkOS)
    }
    if (getStoredAuthProvider() === AuthTokenProvider.WorkOS) callback(event, session)
    else if (event === 'SIGNED_OUT' && !getStoredAuthProvider()) {
      void getSupabaseSession()
        .then((supabaseSession) => {
          if (!supabaseSession && !getStoredAuthProvider()) callback(event, null)
        })
        .catch(() => undefined)
    }
  })

  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    const activeProvider = getStoredAuthProvider()
    if (activeProvider === AuthTokenProvider.WorkOS) return

    if (session) setStoredAuthProvider(AuthTokenProvider.Supabase)
    if (event === 'SIGNED_OUT' && activeProvider === AuthTokenProvider.Supabase) clearStoredAuthProvider()

    const normalizedEvent: AuthStateChangeEvent =
      event === 'INITIAL_SESSION'
        ? 'INITIAL_SESSION'
        : event === 'SIGNED_OUT'
          ? 'SIGNED_OUT'
          : event === 'SIGNED_IN'
            ? 'SIGNED_IN'
            : 'TOKEN_REFRESHED'
    callback(normalizedEvent, session ? toNormalizedSupabaseSession(session) : null)
  })

  return {
    unsubscribe: () => {
      workosSubscription.unsubscribe()
      data.subscription.unsubscribe()
    },
  }
}

export const logout = async (dispatch?: AppDispatch) => {
  clearSentryUser()
  const { store, persistor } = await import('@/store')
  const appDispatch = dispatch ?? store.dispatch
  const provider = getCurrentAuthProvider()
  let signOutError: Error | null = null

  if (provider === AuthTokenProvider.WorkOS) {
    await workosAuthClient.signOut()
    await supabase.auth.signOut({ scope: 'local' })
  } else {
    const { error } = await supabase.auth.signOut({ scope: 'local' })
    await workosAuthClient.clear()
    if (error) signOutError = error
  }

  localStorage.clear()
  resetTenantState(appDispatch, 'session')
  await persistor.purge()
  localStorage.clear()

  if (signOutError) throw signOutError
}

export const isValidEmail = (email: string | undefined) => {
  if (!email || !email.trim()) return false

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}
